import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress } from '@nextiva/next-ui-components';
import { useQueryClient } from 'react-query';

import { updateRole, createRole, getRoles } from 'api/roleApi';
import { ADMIN_ACCESS, HIDDEN_ROLES, ADMIN_SETTINGS } from 'constants/roles';
import {
  EDIT_ROLE,
  CREATE_ROLE,
  CLONE_ROLE,
  AGENT_USER,
  SUPER_ADMIN,
} from 'constants/drawerTitles';
import { useSnackbar } from 'hooks';

import checkPristine from '../utils/form';

const DrawerFooter = ({
  role,
  drawerTitle,
  drawerOpen,
  initialPermissions,
  editPermissions,
  roleRequestBody,
  roleRequestBody: { name, permissions },
  setRoleNameError,
  optimisticallyUpdateRoleList,
  setRoleRequestBody,
  toggleDrawer,
  DeleteIcon,
}) => {
  const queryClient = useQueryClient();

  const [pristine, setPristine] = useState(true);
  const [sending, setSending] = useState(false);

  const setDrawerSnackbar = useSnackbar(true);
  const setGloablSnackbar = useSnackbar();

  useEffect(() => {
    const isPristine = checkPristine(initialPermissions, permissions);

    setPristine(isPristine);

    switch (true) {
      case drawerTitle === CREATE_ROLE:
        if (name !== '') setPristine(false);
        break;
      case role:
      case drawerTitle === EDIT_ROLE:
        if (name !== '' && role.name !== name) {
          setPristine(false);
        }
        break;
      case drawerTitle === CLONE_ROLE:
        if (name !== '') setPristine(false);
        break;
      default:
      // do nothing...
    }

    if (!drawerOpen) setPristine(false);

    // always disable save button if AGENT_USER or SUPER_ADMIN role
    if (drawerTitle === AGENT_USER) setPristine(true);
    if (drawerTitle === SUPER_ADMIN) setPristine(true);
  }, [roleRequestBody]);

  const onErrorHandler = (message) => {
    switch (true) {
      case message.includes('already exists'):
        setDrawerSnackbar({
          message: `A role with this name already exists, change the role's name.`,
          severity: 'error',
        });
        setRoleNameError('Role names must be unique.');
        break;
      case message.includes('required field'):
        setDrawerSnackbar({ message: `Role ${message}.`, severity: 'error' });
        setRoleNameError('Required field');
        break;
      case message.includes('Admin is a reserved'):
        setDrawerSnackbar({ message, severity: 'error' });
        setRoleNameError('Admin is a reserved role name');
        break;
      default:
        setDrawerSnackbar({
          message: 'Unable to update role.',
          severity: 'error',
        });
    }
  };

  const validRoleName = (roleName) => {
    if (roleName.toLowerCase() === 'admin') {
      onErrorHandler(
        'Admin is a reserved role name, give your role a different name.',
      );

      return false;
    }

    return true;
  };

  const invalidateCache = () => {
    queryClient.invalidateQueries(['getRoles']);
    queryClient.invalidateQueries(['getPermissionsMacros']);
    queryClient.invalidateQueries(['getServiceResources']);
  };

  /**
   * To check if Admin Access needs to be set to true or false
   *  when any of the roles under Admin settings tab is toggled
   * @param {
   } editPermissionsObj a copy of editPermissions from state 
   * @returns true or false
   */
  const enableAdminAccess = (editPermissionsObj) => {
    const adminSettingsTab = editPermissionsObj.find(
      (x) => x.title === ADMIN_SETTINGS,
    );

    return adminSettingsTab.macros.some((item) => {
      if (!HIDDEN_ROLES.includes(item.id) && item.toggles) {
        return item.toggles.some((toggle) => {
          if (typeof toggle === 'number') {
            return toggle !== 0;
          }

          return Object.keys(toggle).some((key) => {
            return toggle[key] === true;
          });
        });
      }

      return false;
    });
  };
  const onSaveHandler = () => {
    // Make a copy of roleRequestBody to manipulate the Admin Access
    const permissionsCopy = JSON.parse(JSON.stringify(roleRequestBody));
    // find Admin Access macro
    const adminAccessPermission = permissionsCopy.permissions.find(
      (x) => x.resource_id === ADMIN_ACCESS,
    );

    adminAccessPermission.value[ADMIN_SETTINGS] = enableAdminAccess(
      editPermissions,
    );
    if (validRoleName(permissionsCopy.name) && drawerTitle === EDIT_ROLE) {
      setSending(true);
      updateRole(role.name, permissionsCopy)
        .then((updatedRole) => {
          invalidateCache();
          toggleDrawer();
          setGloablSnackbar({
            message: `${role.name} has been updated!`,
            severity: 'success',
          });
          optimisticallyUpdateRoleList(updatedRole);
          // reset role request body if response is successful
          setRoleRequestBody([]);
        })
        .catch((err) => {
          const { msg } = err.errors;

          onErrorHandler(msg);
          setSending(false);
        });
    }
    if (
      validRoleName(permissionsCopy.name) &&
      (drawerTitle === CREATE_ROLE || drawerTitle === CLONE_ROLE)
    ) {
      /**
       * Can't have the same name of an existing role
       * The roleRequestBody.name property can't be empty
       * The roleRequestBogy.permissions can't be an empty array
       */
      setSending(true);
      createRole(permissionsCopy)
        // TODO: replace nested promises with an async fn()
        .then(() => {
          invalidateCache();
          // get the role from the server
          getRoles(`?name=${roleRequestBody.name}`)
            .then((data) => {
              const newRole = data.result.filter(
                (r) => r.name === roleRequestBody.name,
              )[0];

              optimisticallyUpdateRoleList(newRole);
            })
            .catch(() => {
              setGloablSnackbar({
                message: `${roleRequestBody.name} couldn't be added to the role list, refresh your browser.`,
                severity: 'error',
              });
            });

          setGloablSnackbar({
            message: `${roleRequestBody.name} was successfully created!`,
            severity: 'success',
          });
          setRoleRequestBody([]);
          toggleDrawer();
        })
        .catch((err) => {
          const { msg } = err.errors;

          onErrorHandler(msg);
          setSending(false);
        });
    }
  };

  return (
    <div className="m-drawer-footer">
      <div className="m-drawer-footer__container">
        <div className="m-drawer-footer__container__action-bar">
          <div className="m-drawer-footer__container__action-bar__button">
            <Button
              className="footer__btn"
              onClick={toggleDrawer}
              fullWidth
              variant="contained"
              color="secondary"
              data-testid="PlatformAdminUI-RolesAndPermissions-DrawerFooter-CancelButton"
            >
              Cancel
            </Button>
          </div>
          <div className="m-drawer-footer__container__action-bar__button">
            <Button
              fullWidth
              onClick={onSaveHandler}
              className="footer__btn"
              variant="contained"
              color={pristine ? 'secondary' : 'primary'}
              disabled={pristine}
              data-testid="PlatformAdminUI-RolesAndPermissions-DrawerFooter-SaveButton"
            >
              Save
            </Button>
            {sending && (
              <div className="m-drawer-footer__container__action-bar__spinner">
                <CircularProgress
                  style={{ color: '#009FE0' }}
                  variant="indeterminate"
                  size={36}
                />
              </div>
            )}
          </div>
        </div>
        {DeleteIcon && <DeleteIcon />}
      </div>
    </div>
  );
};

DrawerFooter.propTypes = {
  setRoleRequestBody: PropTypes.func.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
  drawerTitle: PropTypes.string,
};

export default DrawerFooter;
